import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vaadin/accordion/theme/material/vaadin-accordion.js';
import { VaadinStyleLoader } from './vaadin-style-loader.js';
import { elements } from './sdb-elements.js';
/**
 * Accordionitem element, used for each individual accordionitem within an accordion element
 *
 * @slot - This is an unnamed slot (default slot)
 * @slot chipbar - This slot is used to hold the chipbar element
 * @slot buttonbar - This slot is used to hold right aligned buttons
 * @slot gravatar - Needs to be enabled with the property 'gravatar'. Used to hold a gravatar item
 *
 *
 * @prop {String} overline  (part) - optional, used to define an overline (line of text above headline)
 * @prop {String} headline (part) - mandatory, used to define the headline
 * @prop {String} subtitle (part) - optional, used to define a subtitle (line of text below headline)
 * @prop {String} description (part) - optional, used to define a larger piece of text as a description

 * @prop {Boolean} light - optional, used to make text light instead of primary (darkblue)
 * @prop {Boolean} truncate - optional, used to truncate overline, headline, subtitle and description (2 lines)
 * @prop {Boolean} minimal - optional, removes the minimal height

 * @prop {Boolean} date - optional, used for (multiline) date items + new slot
 * @prop {String} opened - optional, used to start with an opened accordionitem by default
 * @prop {String} statusColor - optional, used to display statusbar in specified color (green, orange, red and blue).
 *
 * @prop {String} avatar - optional, used to display a profile picture in a circle wrapper (mainly used for items related to specific users)
 * @prop {String} graphic - optional, used to display an image (thumbnail) in a square wrapper
 * @prop {String} gravatar - optional, used to enable a new slot: 'gravatar' used for gravatar items
 * @prop {String} icon - optional, used to display a Fontawesome icon
 * @prop {Boolean} checkable - optional, when true it will show a checkbox with controlled by the checked property.
 * @prop {Boolean} checkboxDisabled - optional, when true it disable the checkbox.
 *
 */
let SDBContentAccordionItem = class SDBContentAccordionItem extends LitElement {
    constructor() {
        super();
        this.overline = '';
        this.headline = '';
        this.subtitle = '';
        this.description = '';
        this.graphic = '';
        this.avatar = '';
        this.icon = '';
        this.gravatar = false;
        this.date = false;
        this.opened = false;
        this.checkable = false;
        this.checkboxDisabled = false;
        this.checked = false;
        this.locked = false;
        this.statusColor = '';
        this.openedOnce = false;
        VaadinStyleLoader.registerAllStyles();
    }
    render() {
        return html `
            <!-- accordionItem images yes/no -->
            <vaadin-accordion-panel
                class="sdb-content-accordionitem"
                @opened-changed="${(e) => {
            this._openedChanged(e.detail.value);
        }}"
                ?opened=${this.opened}
            >
                <section slot="summary" class="summary">
                    <div class="statusBar ${this.statusColor}"></div>
                    <div class="accordionItem">
                        <section class="mainContent">
                            ${this.checkable
            ? html `<div class="accordionCheckboxWrapper">
                                      <div class="accordionCheckbox">
                                          <sdb-content-checkbox
                                              ?checked=${this.checked}
                                              ?disabled=${this.checkboxDisabled}
                                              @checked-changed="${(e) => {
                this._checkedChanged(e);
            }}"
                                          >
                                          </sdb-content-checkbox>
                                      </div>
                                  </div>`
            : html ``}
                            <!-- accordionItem images yes/no -->
                            ${this.graphic
            ? html `<div class="accordionImgWrapper">
                                      <div
                                          style="background-image: url('${this
                .graphic}');"
                                          class="accordionItemGraphic"
                                      ></div>
                                  </div>`
            : html ``}
                            ${this.avatar
            ? html `<div class="accordionImgWrapper">
                                      <div
                                          style="background-image: url('${this
                .avatar}');"
                                          class="accordionItemAvatar"
                                      ></div>
                                  </div>`
            : html ``}
                            ${this.icon
            ? html `<div class="accordionImgWrapper">
                                      <div class="accordionItemIcon">
                                          <sdb-content-icon
                                              size="32"
                                              .icon=${this.icon}
                                          ></sdb-content-icon>
                                      </div>
                                  </div>`
            : html ``}
                            ${this.gravatar
            ? html `<div class="accordionImgWrapper">
                                      <div class="accordionItemGravatar">
                                          <slot
                                              click="${this._toggleOpened}"
                                              name="gravatar"
                                          ></slot>
                                      </div>
                                  </div>`
            : html ``}
                            <!-- accordionItem content (text & chipbar) -->

                            <div class="main" part="main">
                                ${this.overline
            ? html `<div
                                          class="overline"
                                          part="overline"
                                      >
                                          ${this.overline}
                                      </div>`
            : html ``}
                                <div class="headline" part="headline">
                                    ${this.headline}
                                </div>
                                <!-- headline is mandatory, so always displayed -->
                                ${this.subtitle
            ? html `<div
                                          class="subtitle"
                                          part="subtitle"
                                      >
                                          ${this.subtitle}
                                      </div>`
            : html ``}
                                ${this.description
            ? html `<div
                                          class="description"
                                          part="description"
                                      >
                                          ${this.description}
                                      </div>`
            : html ``}
                                ${this.date
            ? html ` <div class="date" part="date">
                                          <slot
                                              @click="${this._toggleOpened}"
                                              name="date"
                                          ></slot>
                                      </div>`
            : html ``}
                            </div>
                        </section>
                        <slot
                            class="accordionItemColumn"
                            @click="${this._toggleOpened}"
                            name="2"
                        ></slot>
                        <slot
                            class="accordionItemColumn"
                            @click="${this._toggleOpened}"
                            name="3"
                        ></slot>
                        <slot
                            class="accordionItemColumn"
                            @click="${this._toggleOpened}"
                            name="4"
                        ></slot>
                    </div>
                    ${this.locked
            ? html ``
            : html ` <div class="toggle">
                              <sdb-content-icon
                                  size="16"
                                  icon="fal fa-chevron-down"
                              ></sdb-content-icon>
                          </div>`}
                    <div id="accordionItemChipBar" class="accordionItemChipBar">
                        <slot
                            click="${this._toggleOpened}"
                            name="chipbar"
                            class="chipbar"
                        ></slot>
                        <slot
                            click="${this._toggleOpened}"
                            name="buttonbar"
                            class="buttonbar"
                        ></slot>
                    </div>
                </section>
                <slot class="accordionOpened"></slot>
            </vaadin-accordion-panel>
        `;
    }
    _toggleOpened() {
        this.opened = !this.opened;
    }
    _openedChanged(opened) {
        this.opened = opened;
        this.dispatchEvent(new CustomEvent('opened-changed', {
            detail: { value: opened },
        }));
        if (opened) {
            this.openedOnce = true;
            this.dispatchEvent(new CustomEvent('opened', {
                detail: {},
                bubbles: true,
                composed: true,
            }));
        }
        if (this.openedOnce) {
            this.dispatchEvent(new CustomEvent('iron-resize', {
                detail: {},
                bubbles: true,
                composed: true,
            }));
        }
    }
    _checkedChanged(e) {
        this.checked = e.detail.value;
        this.dispatchEvent(new CustomEvent('checked-changed', {
            detail: e.detail,
        }));
    }
};
SDBContentAccordionItem.styles = [
    elements,
    css `
            :host {
                display: block;
                width: 100%;
                color: var(--text-primary);
                min-height: var(--sdb-accordionitem-min-height, auto);
            }

            vaadin-accordion-panel {
                position: relative;
                height: 100%;
                min-height: var(--sdb-accordionitem-min-height, auto);
            }

            vaadin-accordion-panel section.summary {
                /* Needed for the after and before space to be positioned per panel instead of the entire accordion */
                cursor: pointer;
                height: calc(
                    100% -
                        (2 * var(--sdb-accordionitem-summary-vertical-padding))
                );
                min-height: var(--sdb-accordionitem-min-height, auto);
                margin-right: 0;
                display: grid;
                grid-template-rows: 1fr; /* rows will use full available height */
                grid-template-columns: 1fr 50px;
                grid-template-areas:
                    'content toggle'
                    'chipbar chipbar';
            }

            :host(:last-child) vaadin-accordion-panel::after {
                height: 1px;
            }

            vaadin-accordion-panel[opened]::after {
                content: '';
                display: block;
                margin: 0 auto;
                margin-left: -20px;
                width: calc(100% + 40px);
                height: var(--sdb-accordionitem-before-after-height);
                background: var(--body, white);
                position: absolute;
                bottom: -1px;
                z-index: 1;
            }

            :host(:last-child) vaadin-accordion-panel[opened]::after {
                height: 0;
            }

            vaadin-accordion-panel[opened]::before {
                content: '';
                display: block;
                margin: 0 auto;
                margin-left: -20px;
                width: calc(100% + 40px);
                height: var(--sdb-accordionitem-before-after-height);
                background: var(--body, white);
                position: absolute;
                top: -4.5px;
                z-index: 1;
            }

            :host(:first-child) vaadin-accordion-panel[opened]::before {
                height: 0;
            }

            vaadin-accordion-panel[opened] .accordionItemColumn {
                display: none;
            }

            :host([showHover]) {
                --sdb-accordion-background-hover: var(--element-lightgrey);
            }

            :host([light]) section {
                color: var(--text-light);
            }

            :host([truncate]) section.mainContent div.main div {
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
            }

            :host([date]:hover) .outer {
                cursor: default;
                background: transparent;
            }

            .accordionItem {
                grid-area: content;
                display: grid;
                grid-auto-columns: 1fr;
                grid-auto-flow: column;
                align-self: var(--sdb-accordionitem-content-align, center);
                justify-content: flex-start;
                align-items: stretch;
                gap: 0 15px;
                padding: 2.5px 0;
            }

            .accordionItem > * {
                width: 100%;
            }

            :host([disabled]) {
                pointer-events: none;
            }

            :host([disabled]) .accordionItemContent {
                color: var(--element-disabled);
            }

            :host([disabled]:hover) {
                background: transparent;
            }

            [part='summary-content'] {
                display: block !important;
                width: 100%;
            }

            .accordionItemContent {
                grid-area: content;
                display: grid;
                grid-auto-columns: 1fr; /* columns will use full available width */
                grid-auto-flow: column;
                justify-content: flex-start; /* makes sure the content is left aligned */
                align-items: stretch; /* makes sure the items use the full available height of the row */
                gap: 0 15px;
            }

            .toggle {
                grid-area: toggle;
                display: grid;
                align-self: var(--sdb-accordionitem-main-align, center);
                justify-items: center;
                align-items: start;
                pointer-events: none;
            }

            .toggle sdb-content-icon {
                transition: rotate 0.2s ease;
            }

            :host([date]) .toggle {
                align-self: start;
            }

            vaadin-accordion-panel[opened] .toggle sdb-content-icon {
                transform: rotate(180deg);
            }

            section.image {
                width: 75px;
                display: inline-flex;
            }

            section.mainContent {
                display: grid;
                grid-template-columns: auto auto 1fr;
                grid-template-areas: 'checkbox image main';
            }

            section.mainContent .main {
                grid-area: main;
                display: flex;
                justify-content: var(--sdb-accordionitem-main-justify, center);
                flex-direction: column;
                padding-left: 10px;
                overflow-x: hidden;
            }

            .accordionItemColumn::slotted(*) {
                padding: 0 15px;
                align-self: var(--sdb-accordionitem-main-align, center);
            }

            :host([date]) .accordionItemColumn::slotted(*) {
                align-self: start;
                font-weight: 300;
            }

            .accordionItemContent > * {
                /* makes sure all the optional items use the full width of the accordionItem content wrapper */
                width: 100%;
            }

            .accordionOpened {
                display: grid;
            }

            .accordionItemChipBar {
                grid-area: chipbar;
                display: flex;
                place-items: start;
                padding-left: 10px;
                padding-right: 10px;
            }

            :host([date]) .accordionItemChipBar .buttonbar {
                width: 100%;
                justify-content: end;
                padding-right: 40px;
            }

            .accordionItemChipBar::slotted(*) {
                padding: 15px 0;
            }

            :host([icon]) #accordionItemChipBar,
            :host([graphic]) #accordionItemChipBar,
            :host([avatar]) #accordionItemChipBar,
            :host([gravatar]) #accordionItemChipBar {
                margin-left: calc(
                    var(--chipMarginLeft, 0px) + var(--chipExtraMarginLeft, 0px)
                );
                --chipMarginLeft: 60px;
            }

            :host([checkable]) #accordionItemChipBar {
                margin-left: calc(
                    var(--chipMarginLeft, 0px) + var(--chipExtraMarginLeft, 0px)
                );
                --chipExtraMarginLeft: 40px;
            }

            .accordionCheckboxWrapper {
                grid-area: checkbox;
                display: flex;
                width: auto;
                align-items: center;
            }

            .accordionCheckbox {
                width: 50px;
                margin-top: 3px;
                display: flex;
                justify-content: center;
            }

            .accordionImgWrapper {
                grid-area: image;
                display: flex;
                justify-content: var(--sdb-accordionitem-icon-justify, start);
                align-items: var(--sdb-accordionitem-icon-align, start);
            }

            .accordionItemAvatar,
            .accordionItemGraphic,
            .accordionItemIcon,
            .accordionItemGravatar {
                grid-template-areas: image;
                width: 60px;
                height: 60px;
                margin: 3px 0px 0px
                    var(--sdb-accordionitem-icon-padding-left, 10px);
                background-size: cover;
                background-position: center center;
                justify-content: start;
                align-items: center;
            }

            .accordionItemIcon {
                width: 60px;
                height: 40px;
            }

            .accordionItemIcon {
                background: transparent;
                display: flex;
                justify-content: var(--sdb-accordionitem-icon-justify, start);
                align-items: center;
            }

            .accordionItemAvatar {
                border-radius: 50%; /*makes sure user avatars are displayed in circle instead of square*/
            }

            .overline,
            .headline,
            .subtitle,
            .description,
            .chipbar,
            .buttonbar {
                display: flex;
                align-items: center;
            }

            .overline {
                font-size: 0.85em;
                text-transform: uppercase;
            }

            .description {
                margin: 15px 15px 15px 0;
                font-weight: 300;
            }

            .overline + .headline {
                /* makes sure the overline is 'glued on top of' the headline if only these 2 are used */
                align-items: flex-start;
            }

            .headline + .subtitle {
                align-items: flex-start;
            }

            .overline,
            .subtitle,
            .description {
                font-weight: 300;
            }

            :host vaadin-accordion-panel {
                padding: 0
                    var(
                        --sdb-accordionitem-vaadin-accordion-panel-padding,
                        20px
                    )
                    0;
            }

            :host([locked]) vaadin-accordion-panel {
                pointer-events: none;
            }

            :host .statusBar {
                display: block;
                height: 100%;
                width: 10px;
                background: transparent;
                position: absolute;
                top: 0;
                left: 0;
                border-bottom: 1px solid transparent;
                border-top-left-radius: 2px;
                border-bottom-left-radius: 2px;
            }

            .statusBar.green {
                background: var(--success-color);
            }

            .statusBar.grey {
                background: var(--element-darkgrey);
            }

            .statusBar.orange {
                background: var(--warning-color);
            }

            .statusBar.red {
                background: var(--alert-color);
            }

            .statusBar.blue {
                background: var(--inprogress-color);
            }

            .sdb-content-accordionitem {
                background-color: var(
                    --sdb-content-accordionitem-background-color,
                    white
                );
                color: var(--sdb-content-accordionitem-color);
            }

            @media only screen and (max-width: 767px) {
                /* For mobile phones: */

                vaadin-accordion-panel section.summary {
                    grid-template-columns: 1fr 25px;
                }

                .accordionItemContent {
                    grid-auto-flow: row;
                }

                :host vaadin-accordion-panel {
                    padding: 0 10px;
                }

                .accordionItem {
                    grid-auto-flow: row;
                }

                :host([date]) .accordionItemChipBar .buttonbar {
                    padding-right: 10px;
                }
            }
        `,
];
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "overline", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "headline", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "subtitle", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "description", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "graphic", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "avatar", void 0);
__decorate([
    property({ type: String })
], SDBContentAccordionItem.prototype, "icon", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentAccordionItem.prototype, "gravatar", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "date", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "opened", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "checkable", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "checkboxDisabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], SDBContentAccordionItem.prototype, "locked", void 0);
__decorate([
    property({ type: String, reflect: true })
], SDBContentAccordionItem.prototype, "statusColor", void 0);
__decorate([
    state()
], SDBContentAccordionItem.prototype, "openedOnce", void 0);
SDBContentAccordionItem = __decorate([
    customElement('sdb-content-accordionitem')
], SDBContentAccordionItem);
export { SDBContentAccordionItem };
//# sourceMappingURL=sdb-content-accordionitem.js.map